import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api } from "../api.ts";
import { date, dateTime } from "../format.ts";
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  Dialog,
  Empty,
  ErrorText,
  Field,
  Input,
  Loading,
  Notice,
  Page,
  Select,
  Stat,
  Table,
  Td,
} from "../ui.tsx";

interface AccessInvite {
  id: string;
  email: string;
  company: string;
  created_at: string;
  sent_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
}

/** The link an invite email carries, to send another way too. */
function InviteLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <>
      <code className="rounded-control bg-surface-2 px-3 py-2 text-xs break-all">{link}</code>
      <div>
        <Button onClick={() => navigator.clipboard.writeText(link).then(() => setCopied(true))}>
          {copied ? "Copied" : "Copy link"}
        </Button>
      </div>
    </>
  );
}

/**
 * Lets one company in while sign-up is closed: ninekeep emails a link that sets up the company's
 * own account on Recover with no fees, and shows the link here to send another way too.
 */
function InviteDialog({
  open,
  initial,
  onClose,
}: {
  open: boolean;
  initial: { email: string; company: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...initial, free: true });
  const [link, setLink] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => api.post<{ link: string }>("/operator/access-invites", form),
    onSuccess: (r) => {
      setLink(r.link);
      qc.invalidateQueries({ queryKey: ["operator-access-invites"] });
    },
  });
  const close = () => {
    setLink(null);
    send.reset();
    onClose();
  };
  return (
    <Dialog
      open={open}
      title={link ? "Invite sent" : "Invite a company"}
      onClose={close}
      footer={
        link ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="primary"
              busy={send.isPending}
              disabled={!form.email || !form.company}
              onClick={() => send.mutate()}
            >
              Send invite
            </Button>
          </>
        )
      }
    >
      {link ? (
        <>
          <p className="text-sm">
            ninekeep emailed {form.email} a link to set up {form.company} on Recover
            {form.free ? ", free" : ""}. It works once, for 14 days. You can also send it yourself:
          </p>
          <InviteLink link={link} />
        </>
      ) : (
        <>
          <p className="text-sm text-muted">
            They get their own account on Recover: every contract and vendor. Nobody else can use
            the link.
          </p>
          <Field label="Work email">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            )}
          </Field>
          <Field label="Company" hint="The account's name. They can change it in Settings.">
            {(id) => (
              <Input
                id={id}
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
              />
            )}
          </Field>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.free}
              onChange={(e) => setForm({ ...form, free: e.target.checked })}
            />
            <span>
              <span className="font-medium">Free</span>
              <span className="block text-muted">
                ninekeep charges them nothing. You can start charging them later from Companies.
              </span>
            </span>
          </label>
          <ErrorText error={send.error} />
        </>
      )}
    </Dialog>
  );
}

/** One sent invite: its state, and while it's open, send it again (a new link) or cancel it. */
function InviteItem({ invite: i }: { invite: AccessInvite }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["operator-access-invites"] });
  const resend = useMutation({
    mutationFn: () => api.post<{ link: string }>(`/operator/access-invites/${i.id}/resend`, {}),
    onSuccess: (r) => {
      setLink(r.link);
      refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/operator/access-invites/${i.id}/cancel`, {}),
    onSuccess: () => {
      setConfirming(false);
      setLink(null);
      refresh();
    },
  });
  const open = !i.accepted_at && !i.revoked_at;
  return (
    <li className="flex flex-col gap-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium">{i.company}</div>
          <div className="text-sm text-muted">
            {i.email} · sent {date(i.sent_at)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {i.accepted_at ? (
            <Badge tone="ok">Joined {date(i.accepted_at)}</Badge>
          ) : i.revoked_at ? (
            <Badge>Cancelled {date(i.revoked_at)}</Badge>
          ) : new Date(i.expires_at) < new Date() ? (
            <Badge tone="danger">Expired</Badge>
          ) : (
            <Badge tone="warn">Waiting</Badge>
          )}
          {open && !confirming && (
            <>
              <Button busy={resend.isPending} onClick={() => resend.mutate()}>
                Send again
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(true)}>
                Cancel invite
              </Button>
            </>
          )}
          {open && confirming && (
            <>
              <Button variant="danger" busy={cancel.isPending} onClick={() => cancel.mutate()}>
                Cancel it
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Keep it
              </Button>
            </>
          )}
        </div>
      </div>
      {open && confirming && (
        <p className="text-sm text-muted">
          The link stops working at once. You can invite {i.email} again later.
        </p>
      )}
      {link && open && (
        <>
          <p className="text-sm">
            ninekeep emailed {i.email} a new link, good for 14 days. The old one no longer works.
          </p>
          <InviteLink link={link} />
        </>
      )}
      <ErrorText error={resend.error ?? cancel.error} />
    </li>
  );
}

/** ninekeep's own team: everyone who joined the waitlist, with what they told us and where from. */
interface Row {
  created_at: string;
  email: string;
  name: string;
  company: string;
  role: string | null;
  company_size: string | null;
  interest: string;
  note: string | null;
  vendors: string | null;
  spend: string | null;
  claimed_before: string | null;
  heard_from: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer: string | null;
  landing_path: string | null;
  signups: number;
}

const WANTS: Record<string, string> = {
  audit: "Never credited for downtime",
  small: "Never credited (small company)",
  terms: "Doesn't know what contracts promise",
  portfolio: "Portfolio companies owed credits",
  advisor: "Clients they advise",
  renewal: "Leverage for a renewal",
  outage: "A recent outage cost them",
  exploring: "Just exploring",
};
const VENDOR: Record<string, string> = {
  aws: "AWS",
  azure: "Azure",
  gcp: "Google Cloud",
  salesforce: "Salesforce",
  atlassian: "Atlassian",
  snowflake: "Snowflake",
  datadog: "Datadog",
  slack: "Slack",
  zoom: "Zoom",
  okta: "Okta",
  other: "Other",
};
const SPEND: Record<string, string> = {
  under_500k: "Under $500k",
  "500k_5m": "$500k–$5M",
  "5m_25m": "$5M–$25M",
  over_25m: "Over $25M",
};
const CLAIMED: Record<string, string> = {
  never: "Never claimed a credit",
  few: "Claimed once or twice",
  regularly: "Claims regularly",
};
const HEARD: Record<string, string> = {
  search: "Search",
  ai: "An AI assistant",
  linkedin: "LinkedIn",
  referral: "A friend or colleague",
  event: "An event",
  other: "Somewhere else",
};
const SIZE: Record<string, string> = {
  under_200: "Under 200",
  "200_2000": "200–2,000",
  over_2000: "Over 2,000",
};
const sourceOf = (r: Row) =>
  [r.utm_source, r.utm_campaign].filter(Boolean).join(" · ") ||
  (r.referrer ? new URL(r.referrer, "https://x").hostname : "Direct");

export function WaitlistAdmin() {
  const q = useQuery({
    queryKey: ["operator-waitlist"],
    queryFn: () => api.get<{ rows: Row[] }>("/operator/waitlist"),
  });
  const invites = useQuery({
    queryKey: ["operator-access-invites"],
    queryFn: () => api.get<{ invites: AccessInvite[] }>("/operator/access-invites"),
  });
  const [inviting, setInviting] = useState<{ email: string; company: string } | null>(null);
  if (q.isLoading) return <Loading />;
  // Anyone outside ninekeep's team gets the same page as any unknown address.
  if (q.error instanceof ApiError && q.error.status === 404)
    return (
      <Page title="Page not found">
        <Card>
          <Empty title="There's no page at this address.">
            <div className="mt-3 flex justify-center">
              <ButtonLink to="/">Go to Home</ButtonLink>
            </div>
          </Empty>
        </Card>
      </Page>
    );
  if (q.error)
    return (
      <Page title="Waitlist">
        <ErrorText error={q.error} />
      </Page>
    );
  const rows = q.data?.rows ?? [];
  const week = rows.filter((r) => Date.now() - new Date(r.created_at).getTime() < 7 * 864e5);
  const bySource = new Map<string, number>();
  for (const r of rows) bySource.set(sourceOf(r), (bySource.get(sourceOf(r)) ?? 0) + 1);
  const top = [...bySource].sort((a, b) => b[1] - a[1])[0];
  const sent = invites.data?.invites ?? [];
  const invited = new Set(
    sent
      .filter((i) => i.accepted_at || (!i.revoked_at && new Date(i.expires_at) > new Date()))
      .map((i) => i.email.toLowerCase()),
  );
  return (
    <Page
      title="Waitlist"
      lead="Everyone who joined from the website, newest first. Only ninekeep's own team sees this page."
      actions={
        <>
          <Button onClick={() => setInviting({ email: "", company: "" })}>Invite a company</Button>
          <Button
            variant="primary"
            disabled={rows.length === 0}
            onClick={async () => {
              // The download needs a fresh step-up; the dialog asks for it, then the file comes.
              await api.get("/operator/waitlist/ready");
              window.location.href = "/api/operator/waitlist.csv";
            }}
          >
            Download spreadsheet
          </Button>
        </>
      }
    >
      {inviting && (
        <InviteDialog
          key={inviting.email}
          open
          initial={inviting}
          onClose={() => setInviting(null)}
        />
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="On the list" value={rows.length} tone="accent" />
        <Stat label="Joined this week" value={week.length} />
        <Stat label="Top source" value={top ? top[1] : "—"} note={top ? top[0] : "No one yet"} />
      </div>
      <Card flush>
        <Table
          head={["Joined", "Person", "Company", "Problem", "Came from", "Note", ""]}
          empty={
            rows.length === 0 && (
              <Empty title="No one has joined yet.">
                People who fill in the form on the website show up here.
              </Empty>
            )
          }
        >
          {rows.map((r) => (
            <tr key={r.email}>
              <Td className="whitespace-nowrap">
                <span title={dateTime(r.created_at)}>{date(r.created_at)}</span>
                {r.signups > 1 && (
                  <div className="text-xs text-faint">Joined {r.signups} times</div>
                )}
              </Td>
              <Td>
                <div className="font-medium">{r.name}</div>
                <div className="text-muted">{r.email}</div>
              </Td>
              <Td>
                <div>{r.company}</div>
                <div className="text-muted">
                  {[
                    r.role,
                    r.company_size && `${SIZE[r.company_size]} staff`,
                    r.spend && `${SPEND[r.spend] ?? r.spend} a year`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </Td>
              <Td>
                <div>{WANTS[r.interest] ?? r.interest}</div>
                {r.vendors && (
                  <div className="text-muted">
                    {r.vendors
                      .split(",")
                      .map((v) => VENDOR[v] ?? v)
                      .join(", ")}
                  </div>
                )}
                {r.claimed_before && <div className="text-muted">{CLAIMED[r.claimed_before]}</div>}
              </Td>
              <Td>
                <div>{sourceOf(r)}</div>
                {r.heard_from && (
                  <div className="text-muted">Says: {HEARD[r.heard_from] ?? r.heard_from}</div>
                )}
                {r.landing_path && <div className="text-muted">{r.landing_path}</div>}
              </Td>
              <Td className="max-w-xs whitespace-pre-wrap text-muted">{r.note}</Td>
              <Td>
                {invited.has(r.email.toLowerCase()) ? (
                  <Badge tone="accent">Invited</Badge>
                ) : (
                  <Button onClick={() => setInviting({ email: r.email, company: r.company })}>
                    Invite
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
      <Companies />
      {sent.length > 0 && (
        <Card title="Invites" description="Companies let in while sign-up is closed.">
          <Notice tone="accent">
            An invite link works once, for 14 days. Send again for a new link (the old one stops
            working), or cancel an invite nobody should use.
          </Notice>
          <ul className="mt-4 divide-y divide-line">
            {sent.map((i) => (
              <InviteItem key={i.id} invite={i} />
            ))}
          </ul>
        </Card>
      )}
    </Page>
  );
}

interface Company {
  id: string;
  name: string;
  plan: "free_audit" | "recover";
  fees_waived: boolean;
  card_on_file: boolean;
  owner_email: string | null;
  vendors: number;
  contracts: number;
  reminded_at: string | null;
  created_at: string;
}

/** A company with no contract yet: email its owner the "upload a contract" reminder now. */
function RemindButton({ company: c }: { company: Company }) {
  const qc = useQueryClient();
  const remind = useMutation({
    mutationFn: () => api.post<{ to: string }>(`/operator/companies/${c.id}/setup-reminder`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operator-companies"] }),
  });
  const recent = c.reminded_at && Date.now() - new Date(c.reminded_at).getTime() < 864e5;
  return (
    <div className="mt-2 flex flex-col items-start gap-1">
      {!recent && (
        <Button busy={remind.isPending} onClick={() => remind.mutate()}>
          Remind to upload
        </Button>
      )}
      {c.reminded_at && (
        <span className="text-xs text-muted">Reminded {dateTime(c.reminded_at)}</span>
      )}
      <ErrorText error={remind.error} />
    </div>
  );
}

type Billing = "audit" | "paying" | "free";
const billingOf = (c: Company): Billing =>
  c.plan === "free_audit" ? "audit" : c.fees_waived ? "free" : "paying";
const BILLING: Record<Billing, { label: string; plan: Company["plan"]; free: boolean }> = {
  audit: { label: "Free audit", plan: "free_audit", free: false },
  paying: { label: "Recover, paying", plan: "recover", free: false },
  free: { label: "Recover, free", plan: "recover", free: true },
};

/** Every company with an account, and whether ninekeep charges it. */
function Companies() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["operator-companies"],
    queryFn: () => api.get<{ companies: Company[] }>("/operator/companies"),
  });
  const set = useMutation({
    mutationFn: (v: { id: string; billing: Billing }) =>
      api.put(`/operator/companies/${v.id}/billing`, {
        plan: BILLING[v.billing].plan,
        free: BILLING[v.billing].free,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operator-companies"] }),
  });
  if (!q.data || q.data.companies.length === 0) return null;
  return (
    <Card
      title="Companies"
      description="Everyone with an account. Choose who pays: Recover, free means ninekeep charges them nothing."
      flush
    >
      <ErrorText error={set.error} />
      <Table head={["Company", "Owner", "Contracts", "Vendors", "Plan"]}>
        {q.data.companies.map((c) => (
          <tr key={c.id}>
            <Td>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-muted">Joined {date(c.created_at)}</div>
            </Td>
            <Td className="text-muted">{c.owner_email ?? "—"}</Td>
            <Td>
              {c.contracts}
              {c.contracts === 0 && c.owner_email && <RemindButton company={c} />}
            </Td>
            <Td>{c.vendors}</Td>
            <Td>
              <Select
                aria-label={`Plan for ${c.name}`}
                className="w-48"
                value={billingOf(c)}
                disabled={set.isPending && set.variables?.id === c.id}
                onChange={(e) => set.mutate({ id: c.id, billing: e.target.value as Billing })}
              >
                {Object.entries(BILLING).map(([id, b]) => (
                  <option key={id} value={id}>
                    {b.label}
                  </option>
                ))}
              </Select>
              {billingOf(c) === "paying" && (
                <div className="mt-1 text-xs text-muted">
                  {c.card_on_file
                    ? "Card saved with Stripe"
                    : "No card yet: not billed until they add one"}
                </div>
              )}
            </Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}
